import { ImagePlus, RefreshCw, X } from "lucide-react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Constants } from "src/constant";

const DEFAULT_MAX_BYTES = Constants.HAPPENING_NOW_IMAGE_MAX_BYTES;

interface ImageDropzoneProps {
  /** When set, the dropzone shows this URL as the current image (edit flow). */
  initialImageUrl?: string | null;
  file: File | null;
  onFileChange: (file: File | null) => void;
  onError: (message: string | null) => void;
  /** Required only when there is no initial image (i.e. create flow). */
  required?: boolean;
  /** Max upload size in bytes. Defaults to the Happening Now 2 MB cap. */
  maxBytes?: number;
  /** Helper copy shown in the empty state. */
  hint?: string;
}

export const ImageDropzone: React.FC<ImageDropzoneProps> = ({
  initialImageUrl,
  file,
  onFileChange,
  onError,
  required,
  maxBytes = DEFAULT_MAX_BYTES,
  hint = "or click to choose — 2 MB max, 16:9 looks best",
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);

  useEffect(() => {
    if (!file) {
      setPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const handleFiles = useCallback(
    (list: FileList | null) => {
      onError(null);
      const next = list?.[0] ?? null;
      if (!next) return;
      if (!next.type.startsWith("image/")) {
        onError("That file isn't an image. PNG, JPG, or WebP please.");
        return;
      }
      if (next.size > maxBytes) {
        const mb = Math.round(maxBytes / (1024 * 1024));
        onError(`Image is too big — keep it under ${mb} MB.`);
        return;
      }
      onFileChange(next);
    },
    [onError, onFileChange, maxBytes],
  );

  const onDrop = useCallback(
    (e: React.DragEvent<HTMLLabelElement>) => {
      e.preventDefault();
      setDragOver(false);
      handleFiles(e.dataTransfer.files);
    },
    [handleFiles],
  );

  const visibleUrl = previewUrl ?? initialImageUrl ?? null;

  const clear = () => {
    onFileChange(null);
    onError(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  return (
    <div className="flex flex-col gap-2">
      <label
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        className={[
          "group relative flex min-h-[180px] cursor-pointer items-center justify-center overflow-hidden rounded-2xl border-4 border-dashed bg-white text-center transition-colors",
          dragOver
            ? "border-[#14B8A6] bg-[#ECFDF5]"
            : "border-[#E5E5E5] hover:border-[#14B8A6]",
        ].join(" ")}
      >
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          required={required && !visibleUrl}
          onChange={(e) => handleFiles(e.target.files)}
          className="sr-only"
        />
        {visibleUrl ? (
          <>
            <img
              src={visibleUrl}
              alt=""
              className="h-full max-h-72 w-full object-contain"
            />
            <span className="pointer-events-none absolute bottom-2 left-1/2 -translate-x-1/2 rounded-full bg-black/70 px-3 py-1 text-xs font-extrabold text-white opacity-0 transition-opacity group-hover:opacity-100">
              <RefreshCw className="mr-1 inline h-3 w-3" />
              Replace image
            </span>
          </>
        ) : (
          <div className="flex flex-col items-center gap-2 px-6 py-10 text-[#777777]">
            <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#ECFDF5] text-[#0F766E] shadow-[0_4px_0_#A7F3D0]">
              <ImagePlus className="h-6 w-6" aria-hidden />
            </span>
            <span className="text-base font-extrabold text-[#3C3C3C]">
              Drop an image here
            </span>
            <span className="text-sm font-bold">{hint}</span>
          </div>
        )}
      </label>

      {file && (
        <div className="flex items-center justify-between gap-2 rounded-xl bg-[#F7F7F7] px-3 py-2 text-xs font-bold text-[#3C3C3C]">
          <span className="truncate">
            {file.name} · {(file.size / 1024).toFixed(0)} KB
          </span>
          <button
            type="button"
            onClick={clear}
            className="inline-flex items-center gap-1 rounded-lg bg-white px-2 py-1 font-extrabold text-[#777777] ring-1 ring-[#E5E5E5] hover:text-[#EF4444]"
          >
            <X className="h-3 w-3" />
            Remove
          </button>
        </div>
      )}
    </div>
  );
};
